import React, { useState, useEffect } from 'react';
import { auditLogsApi } from '../lib/api';
import { formatDateTime, capitalizeFirst } from '../lib/utils';
import {
  History, Search, Filter, Eye, User, FileText, Wallet, PiggyBank, Settings
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue
} from '../components/ui/select';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle
} from '../components/ui/dialog';

const AuditTrailPage = () => {
  const [logs, setLogs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(0);
  const [entityType, setEntityType] = useState('all');
  const [action, setAction] = useState('all');
  const [showDetailModal, setShowDetailModal] = useState(false);
  const [selectedLog, setSelectedLog] = useState(null);

  useEffect(() => {
    loadLogs();
  }, [page, entityType, action]);

  const loadLogs = async () => {
    setLoading(true);
    try {
      const params = { skip: page * 50, limit: 50 };
      if (entityType && entityType !== 'all') params.entity_type = entityType;
      if (action && action !== 'all') params.action = action;
      const response = await auditLogsApi.list(params);
      setLogs(response.data.logs);
      setTotal(response.data.total);
    } catch (error) {
      console.error('Failed to load audit logs:', error);
    } finally {
      setLoading(false);
    }
  };

  const getEntityIcon = (type) => {
    switch (type) {
      case 'client': return <User className="w-4 h-4" />;
      case 'loan': return <Wallet className="w-4 h-4" />;
      case 'savings_account': return <PiggyBank className="w-4 h-4" />;
      case 'user': return <Settings className="w-4 h-4" />;
      default: return <FileText className="w-4 h-4" />;
    }
  };

  const getActionColor = (actionType) => {
    if (actionType.includes('create') || actionType.includes('register')) return 'bg-emerald-100 text-emerald-800';
    if (actionType.includes('update') || actionType.includes('approve')) return 'bg-blue-100 text-blue-800';
    if (actionType.includes('delete') || actionType.includes('reject')) return 'bg-red-100 text-red-800';
    if (actionType.includes('login') || actionType.includes('logout')) return 'bg-purple-100 text-purple-800';
    return 'bg-gray-100 text-gray-800';
  };

  return (
    <div className="space-y-6 fade-in" data-testid="audit-trail-page">
      {/* Header */}
      <div>
        <p className="text-xs font-semibold uppercase tracking-[0.2em] text-[#C5A059] mb-2">
          COMPLIANCE & SECURITY
        </p>
        <h1 className="font-display text-3xl font-bold text-[#1f1f1f]">
          Audit Trail
        </h1>
        <p className="text-gray-500 mt-1">
          Searchable history of all system activities and changes
        </p>
      </div>

      {/* Filters */}
      <div className="flex flex-wrap gap-4">
        <div className="flex-1 min-w-64">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-gray-400" />
            <Input
              placeholder="Search by entity ID or user..."
              className="pl-10"
              data-testid="audit-search-input"
            />
          </div>
        </div>

        <Select value={entityType} onValueChange={setEntityType}>
          <SelectTrigger className="w-48" data-testid="entity-type-filter">
            <SelectValue placeholder="All Entities" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All Entities</SelectItem>
            <SelectItem value="client">Clients</SelectItem>
            <SelectItem value="loan">Loans</SelectItem>
            <SelectItem value="savings_account">Savings</SelectItem>
            <SelectItem value="journal_entry">Journal Entries</SelectItem>
            <SelectItem value="user">Users</SelectItem>
          </SelectContent>
        </Select>

        <Select value={action} onValueChange={setAction}>
          <SelectTrigger className="w-48" data-testid="action-filter">
            <SelectValue placeholder="All Actions" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All Actions</SelectItem>
            <SelectItem value="create">Create</SelectItem>
            <SelectItem value="update">Update</SelectItem>
            <SelectItem value="delete">Delete</SelectItem>
            <SelectItem value="approve">Approve</SelectItem>
            <SelectItem value="reject">Reject</SelectItem>
            <SelectItem value="login">Login</SelectItem>
          </SelectContent>
        </Select>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <div className="p-4 border border-gray-200">
          <p className="text-2xl font-bold text-[#1f1f1f]">{total}</p>
          <p className="text-xs uppercase tracking-wider text-gray-500">Total Events</p>
        </div>
        <div className="p-4 border border-gray-200">
          <p className="text-2xl font-bold text-emerald-600">
            {logs.filter(l => l.action.includes('create')).length}
          </p>
          <p className="text-xs uppercase tracking-wider text-gray-500">Created Today</p>
        </div>
        <div className="p-4 border border-gray-200">
          <p className="text-2xl font-bold text-blue-600">
            {logs.filter(l => l.action.includes('approve')).length}
          </p>
          <p className="text-xs uppercase tracking-wider text-gray-500">Approved</p>
        </div>
        <div className="p-4 border border-gray-200">
          <p className="text-2xl font-bold text-purple-600">
            {logs.filter(l => l.action.includes('login')).length}
          </p>
          <p className="text-xs uppercase tracking-wider text-gray-500">Logins</p>
        </div>
      </div>

      {/* Logs Table */}
      <div className="bg-white border border-gray-200 overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full" data-testid="audit-logs-table">
            <thead className="bg-gray-50 border-b border-gray-200">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-semibold uppercase tracking-wider text-gray-500">
                  Timestamp
                </th>
                <th className="px-6 py-3 text-left text-xs font-semibold uppercase tracking-wider text-gray-500">
                  Action
                </th>
                <th className="px-6 py-3 text-left text-xs font-semibold uppercase tracking-wider text-gray-500">
                  Entity
                </th>
                <th className="px-6 py-3 text-left text-xs font-semibold uppercase tracking-wider text-gray-500">
                  Entity ID
                </th>
                <th className="px-6 py-3 text-left text-xs font-semibold uppercase tracking-wider text-gray-500">
                  User
                </th>
                <th className="px-6 py-3 text-right text-xs font-semibold uppercase tracking-wider text-gray-500">
                  Details
                </th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100">
              {loading ? (
                <tr>
                  <td colSpan={6} className="px-6 py-12 text-center">
                    <div className="w-8 h-8 border-2 border-gray-200 border-t-[#1f1f1f] rounded-full animate-spin mx-auto" />
                  </td>
                </tr>
              ) : logs.length === 0 ? (
                <tr>
                  <td colSpan={6} className="px-6 py-12 text-center text-gray-500">
                    No audit logs found
                  </td>
                </tr>
              ) : (
                logs.map((log) => (
                  <tr key={log.id} className="hover:bg-gray-50">
                    <td className="px-6 py-4 text-sm text-gray-500">
                      {formatDateTime(log.timestamp)}
                    </td>
                    <td className="px-6 py-4">
                      <span className={`px-2.5 py-0.5 text-xs font-medium ${getActionColor(log.action)}`}>
                        {capitalizeFirst(log.action.replace(/_/g, ' '))}
                      </span>
                    </td>
                    <td className="px-6 py-4">
                      <div className="flex items-center gap-2 text-sm text-gray-600">
                        {getEntityIcon(log.entity_type)}
                        <span>{capitalizeFirst(log.entity_type.replace(/_/g, ' '))}</span>
                      </div>
                    </td>
                    <td className="px-6 py-4 text-sm font-mono text-gray-500">
                      {log.entity_id?.substring(0, 8)}...
                    </td>
                    <td className="px-6 py-4 text-sm text-gray-600">
                      {log.user_id?.substring(0, 8)}...
                    </td>
                    <td className="px-6 py-4 text-right">
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => {
                          setSelectedLog(log);
                          setShowDetailModal(true);
                        }}
                        data-testid={`view-log-${log.id}`}
                      >
                        <Eye className="w-4 h-4" />
                      </Button>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>

        {/* Pagination */}
        <div className="px-6 py-4 border-t border-gray-200 flex items-center justify-between">
          <p className="text-sm text-gray-500">
            Showing {page * 50 + 1} to {Math.min((page + 1) * 50, total)} of {total} events
          </p>
          <div className="flex gap-2">
            <Button
              variant="outline"
              size="sm"
              onClick={() => setPage(p => Math.max(0, p - 1))}
              disabled={page === 0}
            >
              Previous
            </Button>
            <Button
              variant="outline"
              size="sm"
              onClick={() => setPage(p => p + 1)}
              disabled={(page + 1) * 50 >= total}
            >
              Next
            </Button>
          </div>
        </div>
      </div>

      {/* Detail Modal */}
      <Dialog open={showDetailModal} onOpenChange={setShowDetailModal}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle className="font-display text-xl">Audit Log Details</DialogTitle>
          </DialogHeader>

          {selectedLog && (
            <div className="space-y-4">
              <div className="grid grid-cols-2 gap-4 text-sm">
                <div>
                  <p className="text-xs uppercase tracking-wider text-gray-500 mb-1">Timestamp</p>
                  <p className="font-medium">{formatDateTime(selectedLog.timestamp)}</p>
                </div>
                <div>
                  <p className="text-xs uppercase tracking-wider text-gray-500 mb-1">Action</p>
                  <span className={`px-2.5 py-0.5 text-xs font-medium ${getActionColor(selectedLog.action)}`}>
                    {capitalizeFirst(selectedLog.action.replace(/_/g, ' '))}
                  </span>
                </div>
                <div>
                  <p className="text-xs uppercase tracking-wider text-gray-500 mb-1">Entity Type</p>
                  <p className="font-medium">{capitalizeFirst(selectedLog.entity_type.replace(/_/g, ' '))}</p>
                </div>
                <div>
                  <p className="text-xs uppercase tracking-wider text-gray-500 mb-1">Entity ID</p>
                  <p className="font-mono text-xs">{selectedLog.entity_id}</p>
                </div>
                <div>
                  <p className="text-xs uppercase tracking-wider text-gray-500 mb-1">User ID</p>
                  <p className="font-mono text-xs">{selectedLog.user_id}</p>
                </div>
                <div>
                  <p className="text-xs uppercase tracking-wider text-gray-500 mb-1">IP Address</p>
                  <p className="font-medium">{selectedLog.ip_address || 'N/A'}</p>
                </div>
              </div>

              {selectedLog.details && Object.keys(selectedLog.details).length > 0 && (
                <div>
                  <p className="text-xs uppercase tracking-wider text-gray-500 mb-2">Additional Details</p>
                  <pre className="p-4 bg-gray-50 border border-gray-200 text-xs overflow-auto max-h-48">
                    {JSON.stringify(selectedLog.details, null, 2)}
                  </pre>
                </div>
              )}
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default AuditTrailPage;
